import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, updateBirthDate } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { goalService } from '../services/goalService';
import { Goal, GoalCategory, GoalStatus, GoalFormData } from '../types/Goal';
import './Lifetime.css';

interface LifetimeProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

interface LifetimeData {
  birthDate: string;
  lifeExpectancy: number;
}


interface GoalModalData {
  isOpen: boolean;
  weekNumber?: number;
  yearNumber?: number;
  goal?: Goal;
}

interface WeekPopoverData {
  isOpen: boolean;
  weekNumber?: number;
  yearNumber?: number;
  position?: { x: number; y: number };
}

const Lifetime: React.FC<LifetimeProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [lifetimeData, setLifetimeData] = useState<LifetimeData>({
    birthDate: '',
    lifeExpectancy: 70
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [lifetimeGoals, setLifetimeGoals] = useState<Goal[]>([]);
  const [goalModal, setGoalModal] = useState<GoalModalData>({ isOpen: false });
  const [weekPopover, setWeekPopover] = useState<WeekPopoverData>({ isOpen: false });
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);
  const [collapsedYears, setCollapsedYears] = useState<Set<number>>(new Set());
  const [hidePastYears, setHidePastYears] = useState(true);
  const [showStatsSection, setShowStatsSection] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Load birth date and life expectancy from user and fetch goals
  useEffect(() => {
    if (user) {
      if (user.birth_date) {
        setLifetimeData({
          birthDate: user.birth_date || '',
          lifeExpectancy: user.life_expectancy ?? 70
        });
        setIsConfigured(true);
      } else {
        setLifetimeData({
          birthDate: '',
          lifeExpectancy: user.life_expectancy ?? 70
        });
        setIsConfigured(false);
      }
      loadLifetimeGoals();
    }
  }, [user]);

  // Load lifetime goals from the backend
  const loadLifetimeGoals = async () => {
    if (!user) return;
    
    setIsLoadingGoals(true);
    try {
      const response = await goalService.getUserGoals({
        user_id: user.id.toString()
      });
      
      if (response.success && response.goals) {
        const convertedGoals = response.goals.map((goal: any) => goalService.convertFirebaseGoal(goal));
        setLifetimeGoals(convertedGoals);
      }
    } catch (error) {
      console.error('Error loading lifetime goals:', error);
    } finally {
      setIsLoadingGoals(false);
    }
  };

  const handleSaveConfiguration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lifetimeData.birthDate || !user) return;

    try {
      // Save birth date to database
      const updatedUser = await updateBirthDate(lifetimeData.birthDate);
      updateUser(updatedUser);
      setIsConfigured(true);
      setShowForm(false);
    } catch (error) {
      console.error('Failed to save birth date:', error);
      // Still allow local configuration even if save fails
      setIsConfigured(true);
      setShowForm(false);
    }
  };

  const calculateLifeStats = () => {
    if (!lifetimeData.birthDate) return null;

    const birthDate = new Date(lifetimeData.birthDate);
    const today = new Date();
    const expectedDeathDate = new Date(birthDate);
    expectedDeathDate.setFullYear(birthDate.getFullYear() + lifetimeData.lifeExpectancy);

    // Calculate days
    const totalDays = Math.floor((expectedDeathDate.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
    const livedDays = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, totalDays - livedDays);

    // Calculate years, months, days lived
    const ageInMs = today.getTime() - birthDate.getTime();
    const ageInYears = Math.floor(ageInMs / (1000 * 60 * 60 * 24 * 365.25));
    const ageInMonths = Math.floor((ageInMs % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44));
    const ageInDays = Math.floor((ageInMs % (1000 * 60 * 60 * 24 * 30.44)) / (1000 * 60 * 60 * 24));

    // Calculate weeks
    const totalWeeks = Math.floor(totalDays / 7);
    const livedWeeks = Math.floor(livedDays / 7);
    const remainingWeeks = Math.max(0, totalWeeks - livedWeeks);

    return {
      totalDays,
      livedDays,
      remainingDays,
      totalWeeks,
      livedWeeks,
      remainingWeeks,
      ageInYears,
      ageInMonths,
      ageInDays,
      percentageLived: Math.min(100, (livedDays / totalDays) * 100),
      expectedDeathDate: expectedDeathDate.toLocaleDateString()
    };
  };

  // Handle goal creation/editing
  const handleCreateGoal = async (goalData: GoalFormData, weekNumber: number, calendarYear: number) => {
    if (!user) return;

    try {
      // Add lifetime-specific data to the goal
      const lifetimeGoalData = {
        ...goalData,
        category: GoalCategory.OTHER,
        notes: `${goalData.notes || ''}\nWeek: ${weekNumber + 1}, Calendar Year: ${calendarYear}`.trim()
      };

      const response = await goalService.createGoal(user.id.toString(), lifetimeGoalData);
      
      if (response.success) {
        // Reload goals to get the updated list
        await loadLifetimeGoals();
        setGoalModal({ isOpen: false });
      }
    } catch (error) {
      console.error('Error creating lifetime goal:', error);
    }
  };

  // Handle goal deletion
  const handleDeleteGoal = async (goalId: string) => {
    try {
      const response = await goalService.deleteGoal(goalId);
      if (response.success) {
        await loadLifetimeGoals();
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  // Handle goal click to navigate to goal detail
  const handleGoalClick = (goalId: string) => {
    navigate(`/goals/${goalId}`);
  };

  // Handle week box click to show popover
  const handleWeekClick = (event: React.MouseEvent, weekNumber: number, calendarYear: number) => {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const position = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };

    setSelectedWeek(weekNumber);
    setWeekPopover({
      isOpen: true,
      weekNumber,
      yearNumber: calendarYear, // Now using calendar year directly
      position
    });
  };

  // Handle popover menu actions
  const handleAddGoals = () => {
    if (weekPopover.weekNumber !== undefined && weekPopover.yearNumber !== undefined) {
      setGoalModal({
        isOpen: true,
        weekNumber: weekPopover.weekNumber,
        yearNumber: weekPopover.yearNumber
      });
    }
    setWeekPopover({ isOpen: false });
  };

  const handleGoToWeeks = () => {
    if (weekPopover.weekNumber !== undefined && weekPopover.yearNumber !== undefined) {
      // Calculate the date for this calendar week
      const calendarYear = weekPopover.yearNumber;
      const weekNumber = weekPopover.weekNumber;
      
      // Create date for the start of this calendar week (week 0 = first week of January)
      const weekDate = new Date(calendarYear, 0, 1 + (weekNumber * 7));
      
      // Navigate to weeks page for that specific calendar week
      const weekOfYear = getWeekOfYear(weekDate);
      navigate(`/weeks/${calendarYear}-W${weekOfYear.toString().padStart(2, '0')}`);
    }
    setWeekPopover({ isOpen: false });
  };

  // Helper function to get week of year
  const getWeekOfYear = (date: Date): number => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - startOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
  };

  // Get goals for a specific week
  const getGoalsForWeek = (weekNumber: number): Goal[] => {
    // For now, return empty array since we're not storing week numbers in goals
    // This can be enhanced later if needed by parsing notes or using a different approach
    return [];
  };

  // Get active goals for a specific calendar year based on date overlaps
  const getActiveGoalsForYear = (year: number): Goal[] => {
    if (!lifetimeData.birthDate) return [];

    // Convert life year to calendar year
    const birthDate = new Date(lifetimeData.birthDate);
    const calendarYear = birthDate.getFullYear() + year;
    
    // Create calendar year boundaries (January 1st to December 31st)
    const yearStartDate = new Date(calendarYear, 0, 1); // January 1st
    const yearEndDate = new Date(calendarYear, 11, 31, 23, 59, 59, 999); // December 31st end of day

    return lifetimeGoals.filter(goal => {
      // Goals with date ranges that overlap with this calendar year
      if (goal.startedDate || goal.targetDate) {
        const goalStartDate = goal.startedDate ? new Date(goal.startedDate) : null;
        const goalEndDate = goal.targetDate ? new Date(goal.targetDate) : null;

        // Check if goal period overlaps with calendar year period
        if (goalStartDate && goalEndDate) {
          // Goal has both start and end dates - check for overlap
          return goalStartDate <= yearEndDate && goalEndDate >= yearStartDate;
        } else if (goalStartDate) {
          // Goal has only start date - active if started during or before this calendar year
          return goalStartDate <= yearEndDate;
        } else if (goalEndDate) {
          // Goal has only end date - active if ending during or after this calendar year
          return goalEndDate >= yearStartDate;
        }
      }

      return false;
    });
  };

  // Calculate progress color based on percentage
  const getProgressColor = (progress: number): string => {
    if (progress === 0) return '#e0e0e0'; // gray for not started
    if (progress < 30) return '#dc3545'; // red for low progress
    if (progress < 60) return '#ffc107'; // yellow for medium progress
    if (progress < 90) return '#17a2b8'; // teal for good progress
    return '#28a745'; // green for excellent progress
  };

  // Get calculated progress for a goal - lazy loaded per year
  const [goalProgressMap, setGoalProgressMap] = useState<Record<string, number>>({});
  const [loadingProgressForYears, setLoadingProgressForYears] = useState<Set<number>>(new Set());
  const [yearsWithLoadedProgress, setYearsWithLoadedProgress] = useState<Set<number>>(new Set());
  
  // Load progress for goals in a specific year (lazy loading)
  const loadProgressForYear = async (lifeYear: number) => {
    if (!lifetimeData.birthDate || yearsWithLoadedProgress.has(lifeYear) || !isConfigured) {
      return; // Already loaded, no birth date, or not configured
    }
    
    setLoadingProgressForYears(prev => new Set(prev).add(lifeYear));
    
    try {
      // Get active goals for this year
      const activeGoals = getActiveGoalsForYear(lifeYear);
      
      if (activeGoals.length === 0) {
        // No goals for this year, mark as loaded
        setYearsWithLoadedProgress(prev => new Set(prev).add(lifeYear));
        setLoadingProgressForYears(prev => {
          const next = new Set(prev);
          next.delete(lifeYear);
          return next;
        });
        return;
      }
      
      // Load progress for each goal in this year
      const progressMap: Record<string, number> = {};
      
      await Promise.all(
        activeGoals.map(async (goal) => {
          try {
            const response = await goalService.calculateGoalProgress(goal.id);
            if (response.success && response.progress) {
              // Use calculated progress if available, otherwise fall back to manual progress
              progressMap[goal.id] = response.progress.overall || goal.progress || 0;
            } else {
              progressMap[goal.id] = goal.progress || 0;
            }
          } catch (error) {
            console.warn(`Failed to calculate progress for goal ${goal.id}:`, error);
            // Fall back to manual progress
            progressMap[goal.id] = goal.progress || 0;
          }
        })
      );
      
      // Update progress map with new values
      setGoalProgressMap(prev => ({ ...prev, ...progressMap }));
      
      // Mark this year as loaded
      setYearsWithLoadedProgress(prev => new Set(prev).add(lifeYear));
    } catch (error) {
      console.error(`Error loading progress for year ${lifeYear}:`, error);
    } finally {
      setLoadingProgressForYears(prev => {
        const next = new Set(prev);
        next.delete(lifeYear);
        return next;
      });
    }
  };
  
  // Load progress when a year is expanded
  useEffect(() => {
    if (!isConfigured || !lifetimeData.birthDate) return;
    
    const birthDate = new Date(lifetimeData.birthDate);
    const today = new Date();
    const currentCalendarYear = today.getFullYear();
    const birthCalendarYear = birthDate.getFullYear();
    
    // Check each year - if it's expanded and hasn't loaded progress yet, load it
    for (let calendarYear = birthCalendarYear; calendarYear <= birthCalendarYear + lifetimeData.lifeExpectancy; calendarYear++) {
      const lifeYear = calendarYear - birthCalendarYear;
      const isExpanded = !collapsedYears.has(lifeYear);
      const hasLoadedProgress = yearsWithLoadedProgress.has(lifeYear);
      
      if (isExpanded && !hasLoadedProgress) {
        loadProgressForYear(lifeYear);
      }
    }
  }, [collapsedYears, isConfigured, lifetimeData.birthDate, lifetimeData.lifeExpectancy]);
  
  // Reload progress for expanded years when goals change
  useEffect(() => {
    if (!isConfigured || !lifetimeData.birthDate) return;
    
    const birthDate = new Date(lifetimeData.birthDate);
    const birthCalendarYear = birthDate.getFullYear();
    
    // Reload progress for all currently expanded years
    for (let calendarYear = birthCalendarYear; calendarYear <= birthCalendarYear + lifetimeData.lifeExpectancy; calendarYear++) {
      const lifeYear = calendarYear - birthCalendarYear;
      const isExpanded = !collapsedYears.has(lifeYear);
      
      if (isExpanded) {
        // Clear the loaded flag so it reloads
        setYearsWithLoadedProgress(prev => {
          const next = new Set(prev);
          next.delete(lifeYear);
          return next;
        });
        // Progress will be reloaded by the other useEffect
      }
    }
  }, [lifetimeGoals, isConfigured, lifetimeData.birthDate, lifetimeData.lifeExpectancy, collapsedYears]);
  
  // Load current year's progress automatically on initial page load
  useEffect(() => {
    if (!isConfigured || !lifetimeData.birthDate || lifetimeGoals.length === 0) return;
    
    const birthDate = new Date(lifetimeData.birthDate);
    const today = new Date();
    const currentCalendarYear = today.getFullYear();
    const birthCalendarYear = birthDate.getFullYear();
    const currentLifeYear = currentCalendarYear - birthCalendarYear;
    
    // Check if current year is expanded (it should be by default)
    const isCurrentYearExpanded = !collapsedYears.has(currentLifeYear);
    
    // Load progress for current year if expanded
    // loadProgressForYear will check internally if it's already loaded
    if (isCurrentYearExpanded) {
      loadProgressForYear(currentLifeYear);
    }
  }, [isConfigured, lifetimeData.birthDate, lifetimeGoals.length, collapsedYears]);

  // Initialize collapsed years (collapse past and future years by default)
  useEffect(() => {
    if (isConfigured && lifetimeData.birthDate) {
      const birthDate = new Date(lifetimeData.birthDate);
      const today = new Date();
      
      // Calculate current calendar year relative to birth year
      const currentCalendarYear = today.getFullYear();
      const birthCalendarYear = birthDate.getFullYear();
      const currentLifeYear = currentCalendarYear - birthCalendarYear;
      
      const collapsedYearsSet = new Set<number>();
      
      // Collapse all years before current year (past years)
      for (let year = 0; year < currentLifeYear; year++) {
        collapsedYearsSet.add(year);
      }
      
      // Collapse all years after current year (future years)
      for (let year = currentLifeYear + 1; year < lifetimeData.lifeExpectancy; year++) {
        collapsedYearsSet.add(year);
      }
      
      setCollapsedYears(collapsedYearsSet);
    }
  }, [isConfigured, lifetimeData.birthDate]);

  // Toggle year collapse state
  const toggleYearCollapse = (year: number) => {
    const newCollapsedYears = new Set(collapsedYears);
    if (newCollapsedYears.has(year)) {
      newCollapsedYears.delete(year);
    } else {
      newCollapsedYears.add(year);
    }
    setCollapsedYears(newCollapsedYears);
  };

  // Toggle hide past years
  const toggleHidePastYears = () => {
    setHidePastYears(!hidePastYears);
  };

  // Close options menu and week popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showOptionsMenu && !target.closest('.options-menu-container')) {
        setShowOptionsMenu(false);
      }
      if (weekPopover.isOpen && !target.closest('.week-popover-container')) {
        setWeekPopover({ isOpen: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu, weekPopover.isOpen]);

  const renderLifeVisualization = () => {
    const stats = calculateLifeStats();
    if (!stats) return null;

    // Create years with weeks - now using calendar years
    const weeksPerYear = 52;
    const birthDate = new Date(lifetimeData.birthDate);
    const today = new Date();
    const currentCalendarYear = today.getFullYear();
    const birthCalendarYear = birthDate.getFullYear();
    const deathCalendarYear = birthCalendarYear + lifetimeData.lifeExpectancy;
    
    const yearSections = [];

    // Iterate through calendar years from birth year to expected death year
    for (let calendarYear = birthCalendarYear; calendarYear <= deathCalendarYear; calendarYear++) {
      const lifeYear = calendarYear - birthCalendarYear; // 0-based life year for internal calculations
      const isCurrentYear = calendarYear === currentCalendarYear;
      const isPastYear = calendarYear < currentCalendarYear;
      const isFutureYear = calendarYear > currentCalendarYear;
      const isCollapsed = collapsedYears.has(lifeYear);
      
      // Skip past years if hide toggle is enabled
      if (hidePastYears && isPastYear) {
        continue;
      }
      
      // Calculate how many weeks have been lived in this calendar year
      let yearLivedWeeks;
      if (isCurrentYear) {
        // For current year, calculate which week of the calendar year we're in
        const startOfYear = new Date(calendarYear, 0, 1);
        const daysSinceStartOfYear = Math.floor((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
        yearLivedWeeks = Math.ceil((daysSinceStartOfYear + startOfYear.getDay() + 1) / 7);
        yearLivedWeeks = Math.max(1, Math.min(weeksPerYear, yearLivedWeeks));
      } else if (isPastYear) {
        // For past years, check if the entire year was lived
        if (calendarYear === birthCalendarYear) {
          // Birth year - calculate weeks from birthday to end of year
          const endOfBirthYear = new Date(calendarYear, 11, 31);
          const daysFromBirthToEndOfYear = Math.floor((endOfBirthYear.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
          yearLivedWeeks = Math.ceil(daysFromBirthToEndOfYear / 7);
        } else {
          yearLivedWeeks = weeksPerYear; // Full year lived
        }
      } else {
        // Future year
        if (calendarYear === deathCalendarYear) {
          // Death year - calculate weeks from start of year to expected death
          const expectedDeathDate = new Date(birthDate);
          expectedDeathDate.setFullYear(birthDate.getFullYear() + lifetimeData.lifeExpectancy);
          const startOfDeathYear = new Date(calendarYear, 0, 1);
          const daysFromStartOfYearToDeath = Math.floor((expectedDeathDate.getTime() - startOfDeathYear.getTime()) / (1000 * 60 * 60 * 24));
          yearLivedWeeks = 0; // Future weeks, not lived yet
        } else {
          yearLivedWeeks = 0; // Future year, no weeks lived yet
        }
      }
      
      const activeGoals = getActiveGoalsForYear(lifeYear);
      
      // Create weeks for this calendar year
      const yearWeeks = [];
      for (let week = 0; week < weeksPerYear; week++) {
        // Calculate the actual date for this week
        const weekDate = new Date(calendarYear, 0, 1 + (week * 7));
        
        // Determine if this week has been lived
        let isLived, isCurrentWeek;
        if (isCurrentYear) {
          isLived = week < yearLivedWeeks - 1; // -1 because current week isn't fully lived
          isCurrentWeek = week === yearLivedWeeks - 1; // Current week is at yearLivedWeeks - 1
        } else if (isPastYear) {
          // Check if this week was after birth date
          if (calendarYear === birthCalendarYear) {
            const weekEndDate = new Date(calendarYear, 0, 1 + ((week + 1) * 7));
            isLived = weekEndDate > birthDate;
            isCurrentWeek = false;
          } else {
            isLived = true;
            isCurrentWeek = false;
          }
        } else {
          // Future year
          if (calendarYear === deathCalendarYear) {
            const expectedDeathDate = new Date(birthDate);
            expectedDeathDate.setFullYear(birthDate.getFullYear() + lifetimeData.lifeExpectancy);
            isLived = weekDate < expectedDeathDate;
            isCurrentWeek = false;
          } else {
            isLived = false;
            isCurrentWeek = false;
          }
        }
        
        const weekGoals = getGoalsForWeek(week); // Now using calendar week
        const hasGoals = weekGoals.length > 0;
        
        yearWeeks.push(
          <div
            key={`${calendarYear}-${week}`}
            className={`week-box ${isLived ? 'lived' : 'remaining'} ${isCurrentWeek ? 'current' : ''} ${hasGoals ? 'has-goals' : ''}`}
            title={`${calendarYear}, Week ${week + 1}${isLived ? ' - Lived' : ' - Remaining'}${isCurrentWeek ? ' (Current Week)' : ''}${hasGoals ? `\nGoals: ${weekGoals.map(g => g.title).join(', ')}` : ''}`}
            onClick={(e) => handleWeekClick(e, week, calendarYear)}
          />
        );
      }

      yearSections.push(
        <div key={calendarYear} className="year-section">
          <div 
            className={`year-header ${isPastYear ? 'past' : isFutureYear ? 'future' : 'current'}`}
            onClick={() => toggleYearCollapse(lifeYear)}
          >
            <div className="year-info">
              <span className="year-title">
                {calendarYear} (Age {lifeYear})
                {isCurrentYear && <span className="current-badge">Current</span>}
              </span>
              <div className="year-stats">
                <span className="weeks-lived">{yearLivedWeeks}/{weeksPerYear} weeks</span>
                <button
                  className="year-note-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/notes/year-${calendarYear}`);
                  }}
                  title={`Open notes for ${calendarYear}`}
                >
                  📝
                </button>
              </div>
            </div>
            <span className={`collapse-arrow ${isCollapsed ? 'collapsed' : 'expanded'}`}>
              {isCollapsed ? '▶' : '▼'}
            </span>
          </div>
          
          <div className={`year-content ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            <div className="weeks-grid-year">
              {yearWeeks}
            </div>
            {activeGoals.length > 0 && (
              <div className="year-goals-list">
                <div className="goals-list-header">
                  <h4 className="goals-list-title">Active Goals: ({activeGoals.length})</h4>
                  <button 
                    className="add-goal-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGoalModal({
                        isOpen: true,
                        weekNumber: 0,
                        yearNumber: calendarYear
                      });
                    }}
                    title="Add goal for this year"
                  >
                    +
                  </button>
                </div>
                <div className="goals-list-with-progress">
                  {activeGoals.map((goal) => {
                    const progress = goalProgressMap[goal.id] ?? goal.progress ?? 0;
                    const progressColor = getProgressColor(progress);
                    const isCalculating = loadingProgressForYears.has(lifeYear) && goalProgressMap[goal.id] === undefined;
                    
                    return (
                      <div 
                        key={goal.id} 
                        className="goal-item-with-progress"
                        onClick={() => handleGoalClick(goal.id)}
                        title={`${goal.title} - ${isCalculating ? 'Calculating...' : `${progress}% complete`}`}
                      >
                        <span className="goal-name-progress clickable-goal">
                          {goal.title}
                        </span>
                        {isCalculating ? (
                          <span className="goal-progress-loading">
                            <span className="progress-spinner">⟳</span>
                          </span>
                        ) : (
                          <span 
                            className="goal-progress-badge"
                            style={{ 
                              backgroundColor: progressColor,
                              color: progress === 0 ? '#666666' : '#ffffff'
                            }}
                          >
                            {progress}%
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="life-visualization">
        {/* Stats Section */}
        <div className="lifetime-stats-section">
          <button 
            className="lifetime-stats-section-toggle"
            onClick={() => setShowStatsSection(!showStatsSection)}
          >
            <span className="lifetime-stats-toggle-text">
              /stats
              <span className="lifetime-stats-count">
                {' - '}
                {stats.ageInYears}y {stats.ageInMonths}m {stats.ageInDays}d • {stats.percentageLived.toFixed(1)}%
              </span>
            </span>
            <span className="lifetime-stats-toggle-arrow">{showStatsSection ? '▼' : '▶'}</span>
          </button>
          
          <div 
            className={`lifetime-stats-section-content ${showStatsSection ? 'expanded' : 'collapsed'}`}
            style={{
              maxHeight: showStatsSection ? '500px' : '0px',
              paddingTop: showStatsSection ? '1rem' : '0px',
              paddingBottom: showStatsSection ? '1rem' : '0px',
            }}
          >
            <div className="primary-stats">
              <div className="age-stat">
                <span className="stat-label">Current Age</span>
                <span className="stat-value-large">{stats.ageInYears} years, {stats.ageInMonths} months, {stats.ageInDays} days</span>
              </div>
              <div className="progress-stat">
                <span className="stat-label">Life Progress</span>
                <div className="progress-container">
                  <span className="progress-percentage">{stats.percentageLived.toFixed(1)}%</span>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${stats.percentageLived}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="secondary-stats">
              <div className="days-stats">
                <div className="days-item">
                  <span className="days-value lived-color">{stats.livedDays.toLocaleString()}</span>
                  <span className="days-label">Days Lived</span>
                </div>
                <div className="days-separator">•</div>
                <div className="days-item">
                  <span className="days-value remaining-color">{stats.remainingDays.toLocaleString()}</span>
                  <span className="days-label">Days Remaining</span>
                </div>
              </div>
              
              <div className="goals-overview">
                <span className="goals-total">{lifetimeGoals.length} lifetime goals</span>
                {lifetimeGoals.length > 0 && (
                  <div className="goals-breakdown">
                    <span className="goal-count completed">{lifetimeGoals.filter(g => g.status === GoalStatus.COMPLETED).length} completed</span>
                    <span className="goal-count in-progress">{lifetimeGoals.filter(g => g.status === GoalStatus.IN_PROGRESS).length} in progress</span>
                    <span className="goal-count planned">{lifetimeGoals.filter(g => g.status === GoalStatus.NOT_STARTED).length} planned</span>
                  </div>
                )}
              </div>
            </div>
            
            {stats.percentageLived > 50 && (
              <div className="wisdom-note-condensed">
                💡 You've lived more than half of your expected lifetime. Every moment becomes more precious.
              </div>
            )}
          </div>
        </div>

        <p className="visualization-description">
          Each square represents one week of your life. Click any week to add goals for that time period.
          <br />
          <span className="lived-indicator">■ Lived</span> | 
          <span className="remaining-indicator">■ Remaining</span>
        </p>
        <div className="years-container">
          {yearSections}
        </div>

      </div>
    );
  };

  // Render goal modal
  const renderGoalModal = () => {
    if (!goalModal.isOpen || goalModal.weekNumber === undefined || goalModal.yearNumber === undefined) {
      return null;
    }

    const weekGoals = getGoalsForWeek(goalModal.weekNumber);
    const weekInfo = `${goalModal.yearNumber}, Week ${goalModal.weekNumber + 1}`;

    return (
      <div className="goal-modal-overlay" onClick={() => setGoalModal({ isOpen: false })}>
        <div className="goal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="goal-modal-header">
            <h3>/goals - {weekInfo}</h3>
            <button 
              className="close-btn"
              onClick={() => setGoalModal({ isOpen: false })}
            >
              ×
            </button>
          </div>
          
          <div className="goal-modal-content">
            {weekGoals.length > 0 && (
              <div className="existing-goals">
                <h4>Existing Goals</h4>
                <div className="goals-list">
                  {weekGoals.map(goal => (
                    <div key={goal.id} className="goal-item">
                      <div 
                        className="goal-info clickable-goal"
                        onClick={() => handleGoalClick(goal.id)}
                        title="Click to view goal details"
                      >
                        <h5>{goal.title}</h5>
                        <p>{goal.description}</p>
                        <div className="goal-meta">
                          <span className={`status ${goal.status}`}>{goal.status.replace('_', ' ')}</span>
                          <span className="progress">{goal.progress}%</span>
                        </div>
                      </div>
                      <button 
                        className="delete-goal-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGoal(goal.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="new-goal-form">
              <h4>Add New Goal</h4>
              <GoalForm 
                onSubmit={(goalData) => handleCreateGoal(goalData, goalModal.weekNumber!, goalModal.yearNumber!)}
                onCancel={() => setGoalModal({ isOpen: false })}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Simple goal form component
  const GoalForm: React.FC<{
    onSubmit: (data: GoalFormData) => void;
    onCancel: () => void;
  }> = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState<GoalFormData>({
      title: '',
      description: '',
      category: GoalCategory.PERSONAL,
      targetDate: '',
      startedDate: '',
      notes: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (formData.title.trim()) {
        onSubmit(formData);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="goal-form">
        <div className="form-group">
          <label htmlFor="title">Goal Title:</label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            required
            placeholder="Enter your goal title"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe your goal"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category:</label>
          <select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as GoalCategory }))}
          >
            {Object.values(GoalCategory).map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startedDate">Start Date:</label>
            <input
              type="date"
              id="startedDate"
              value={formData.startedDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startedDate: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label htmlFor="targetDate">Target Date:</label>
            <input
              type="date"
              id="targetDate"
              value={formData.targetDate}
              onChange={(e) => setFormData(prev => ({ ...prev, targetDate: e.target.value }))}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes:</label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes about this goal"
            rows={2}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="save-btn">
            Create Goal
          </button>
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    );
  };

  // Render week popover with smart positioning
  const renderWeekPopover = () => {
    if (!weekPopover.isOpen || !weekPopover.position || weekPopover.weekNumber === undefined || weekPopover.yearNumber === undefined) {
      return null;
    }

    const weekInfo = `${weekPopover.yearNumber}, Week ${weekPopover.weekNumber + 1}`;
    
    // Calculate smart positioning to keep popover within viewport
    const popoverWidth = 200; // Approximate width of the popover
    const popoverHeight = 120; // Approximate height of the popover
    const padding = 16; // Padding from viewport edges
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let { x, y } = weekPopover.position;
    let transformX = '-50%'; // Center by default
    let transformY = '-100%'; // Above by default
    
    // Adjust horizontal position
    if (x - popoverWidth / 2 < padding) {
      // Too close to left edge - align to left
      transformX = '0%';
      x = Math.max(padding, x);
    } else if (x + popoverWidth / 2 > viewportWidth - padding) {
      // Too close to right edge - align to right
      transformX = '-100%';
      x = Math.min(viewportWidth - padding, x);
    }
    
    // Adjust vertical position
    if (y - popoverHeight - padding < 0) {
      // Too close to top - show below instead
      transformY = '10px';
    } else if (y > viewportHeight - padding) {
      // Too close to bottom - adjust upward
      y = viewportHeight - padding;
    }

    return (
      <div 
        className="week-popover-overlay"
        onClick={() => setWeekPopover({ isOpen: false })}
      >
        <div 
          className="week-popover-container"
          style={{
            position: 'fixed',
            left: x,
            top: y,
            transform: `translate(${transformX}, ${transformY})`,
            zIndex: 1001
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="week-popover">
            <div className="week-popover-header">
              <span className="week-popover-title">{weekInfo}</span>
            </div>
            <div className="week-popover-menu">
              <button 
                className="week-popover-option"
                onClick={handleAddGoals}
              >
                🎯 Add Goals
              </button>
              <button 
                className="week-popover-option"
                onClick={handleGoToWeeks}
              >
                📊 Go to Weeks Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="lifetime-container">
        <div className="lifetime-header">
          <div className="header-left">
            <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          </div>
          <h1>/lifetime</h1>
        </div>
        <div className="auth-required">
          <p>Please sign in to access the Lifetime feature.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lifetime-container">
      <div className="lifetime-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
        </div>
        <h1>/lifetime</h1>
        <div className="header-actions">
          {isConfigured && (
            <div className="options-menu-container">
              <button 
                className="options-menu-btn"
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
              >
                ⋯
              </button>
              {showOptionsMenu && (
                <div className="options-menu-popover">
                  <button 
                    className="menu-option"
                    onClick={() => {
                      setShowForm(true);
                      setShowOptionsMenu(false);
                    }}
                  >
                    Configure
                  </button>
                  <button 
                    className="menu-option"
                    onClick={() => {
                      setHidePastYears(!hidePastYears);
                      setShowOptionsMenu(false);
                    }}
                  >
                    {hidePastYears ? 'Show Past Weeks' : 'Hide Past Weeks'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!isConfigured || showForm ? (
        <div className="configuration-section">
          <div className="config-card">
            <h2>Configuration</h2>
            <p>Set your birthday and expected lifespan to visualize your life journey.</p>
            <p style={{ fontSize: '0.875rem', color: '#666666', marginTop: '0.5rem' }}>
              Note: You can also edit your birth date and life expectancy in Settings → Account.
            </p>
            
            <form onSubmit={handleSaveConfiguration} className="lifetime-form">
              <div className="form-group">
                <label htmlFor="birthDate">Your Birthday:</label>
                <input
                  type="date"
                  id="birthDate"
                  value={lifetimeData.birthDate}
                  onChange={(e) => setLifetimeData(prev => ({ ...prev, birthDate: e.target.value }))}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="save-btn">
                  Save
                </button>
                {isConfigured && (
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowForm(false)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      ) : (
        renderLifeVisualization()
      )}
      
      {renderGoalModal()}
      {renderWeekPopover()}
    </div>
  );
};

export default Lifetime;
